
import { Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Grid, Paper, styled, Switch, Table, TableBody, TableCell, TablePagination, TableRow, Typography } from '@mui/material';
import { FC, useContext, useMemo, useState } from 'react';

import { faBell, faEye, faEyeSlash, faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { grey } from '@mui/material/colors';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { DeleteNotificationRequest, NotificationViewed, SiteEventType, Species } from '../api/dtos/dtos';
import AuthContext from '../contexts/auth/AuthContext';
import { NotificationContext } from '../contexts/data/NotificationContext';
import { SpeciesContext } from '../contexts/data/SpeciesContext';
import getNotificationImage from '../functions/notification';
import useServiceStackClient from '../hooks/api/useServiceStackClient';
import useGetWebsiteNotifications from '../hooks/data/useGetWebsiteNotifications';

const ContentWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
}));

const AlertContainer = styled(Box)(() => ({
    display: "flex", 
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    minHeight: "50vh"
}));

const ColumnContainer = styled(Box)(() => ({
    display: "flex",
    flexDirection: "column"
}));

const RowContainer = styled(Box)(() => ({
    display: "flex",
    flexDirection: "row"
}));

const NotificationsPage: FC = () => {
    //
    // -- Hooks --
    //
    const { species: { species } } = useContext(SpeciesContext);
    const { user, openLogin } = useContext(AuthContext);
    const { refresh: refreshNotificationCount } = useContext(NotificationContext);
    const client = useServiceStackClient();
    const navigate = useNavigate();

    //
    // -- States --
    //
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [allSpecies, setAllSpecies] = useState<boolean>(false);
    const [deleteNotificationId, setDeleteNotificationId] = useState<number | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // paginated notification request
    const notificationsRequest = useMemo(() => {
        if (!user || !user?.id) {
            return undefined;
        }

        return {
            userId: user?.id,
            pageNumber: page,
            pageSize: rowsPerPage,
            species: !allSpecies ? species : undefined
        }
    }, [user, page, rowsPerPage, species, allSpecies]);

    // Get notifcations requests
    const { data: notificationData, loading: notificationsDataLoading, refresh }  = useGetWebsiteNotifications(
        notificationsRequest ? notificationsRequest : undefined, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    const toggleIsRead = async (id?: number, value?: boolean) => {
        if (id === undefined || value === undefined) {
            return;
        }

        const request = new NotificationViewed({
            notificationId: id,
            value: value
        });

        await client.post(request);

        await refresh();
        await refreshNotificationCount();
    };

    const handleNotificationDelete = async () => {
        if (deleteNotificationId === null) {
            return;
        }

        try {
            setDeleteLoading(true);

            const request = new DeleteNotificationRequest({
                id: deleteNotificationId,
            });

            await client.delete(request);

            setDeleteNotificationId(null);
            await refresh();
            await refreshNotificationCount();
        } finally {
            setDeleteLoading(false);
        }
    };

    const tableRows = useMemo(() => {
        return (
            notificationData?.userNotifications?.map((notification, index) => (
                <TableRow 
                    key={notification.id ?? index}
                    sx={{
                        background: notification?.isRead ? 'white' : '#90d4ff4f'
                    }}
                >
                    <TableCell 
                        sx={{
                            width: '120px'
                        }}
                    >
                        <img
                            src={getNotificationImage(
                                notification.species,
                                notification.siteEventType
                            )}
                            alt=""
                            width={86}
                        />
                    </TableCell>
                    <TableCell>
                        <ColumnContainer>
                            <Typography 
                                fontWeight={600} 
                                fontSize={18}
                            >
                                {Species[notification?.species]} - {SiteEventType[notification?.siteEventType ?? 0]}
                            </Typography>
                            <Typography 
                                fontWeight={300} 
                                fontSize={14} 
                                color={grey[600]}
                            >
                                {dayjs(notification?.sightingDateEntered).format('hh:mm A - DD/MM/YYYY')}
                            </Typography>
                        </ColumnContainer>
                    </TableCell>
                    <TableCell>
                        <RowContainer justifyContent={'end'}>
                            <Button
                                onClick={async () => await toggleIsRead(notification?.id, !notification?.isRead)}
                            >
                                {notification?.isRead 
                                    ? <FontAwesomeIcon icon={faEyeSlash} /> 
                                    : <FontAwesomeIcon icon={faEye} />}
                            </Button>
                            <Button
                                onClick={() => setDeleteNotificationId(notification?.id ?? null)}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                            <Button
                                onClick={async () => {
                                    await toggleIsRead(notification?.id, true);
                                    await refreshNotificationCount();
                                    navigate(`${notification?.id}`);
                                }}
                            >
                                View
                            </Button>
                        </RowContainer>
                    </TableCell>
                </TableRow>
            )) ?? []
        );
    }, [notificationData]);

    const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSwitchChange = (_event: React.ChangeEvent<HTMLInputElement, Element>, checked: boolean) => {
        setAllSpecies(checked);
    };


    if (!user) {
        return (
            <AlertContainer>
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        maxWidth: 400,
                        width: "100%",
                        textAlign: "center",
                        borderRadius: 2,
                        background: '#F5F5F5'
                    }}
                >
                    <Typography variant="h5" color="text.primary" gutterBottom>
                        Access Restricted
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        You must be logged in to view this page.
                    </Typography>

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => openLogin()}
                        sx={{ color: "white" }}
                    >
                        Log In
                    </Button>
                </Paper>
            </AlertContainer>
        );
    }

    return (
        <ContentWrapper>

            <Card
                sx={{
                    marginY: '30px',
                    paddingTop: '15px'
                }}
            >
                <Grid container spacing={1} alignItems={"center"}>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems={"center"}>
                            <FontAwesomeIcon icon={faBell} style={{ fontSize: '1.2rem', paddingLeft: "10px", paddingRight: "5px" }} />
                            <Typography paddingLeft={"5px"} fontSize={24} >
                                Notifications
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box display="flex" justifyContent={"end"}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={allSpecies}
                                        onChange={handleSwitchChange}
                                    />
                                }
                                label="All Species"
                            />
                            <TablePagination
                                component="div"
                                count={notificationData?.totalCount ?? 0}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Table>
                    <TableBody sx={{ borderTop: '1px solid black' }}>
                        {!notificationsDataLoading && notificationData?.userNotifications && notificationData?.userNotifications?.length !== 0 && (
                            <>
                                {tableRows}
                            </>
                        )}
                    </TableBody>
                </Table>
                { notificationsDataLoading && (
                    <Box sx={{display: "flex", justifyContent: "center", width: "auto", paddingTop: "20px"}}>
                        <CircularProgress />
                    </Box>
                )}
                {(!notificationsDataLoading && notificationData?.userNotifications && notificationData?.userNotifications?.length === 0) && (
                    <Typography sx={{padding: "20px"}}>
                        No notifications were found that match the current filters.
                    </Typography>
                )}
                
                <Dialog
                    open={deleteNotificationId !== null}
                    onClose={() => setDeleteNotificationId(null)}
                >
                    <DialogTitle sx={{ color: 'black' }}>
                        Delete Notification
                    </DialogTitle>

                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this notification?
                            This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions
                        sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between' 
                        }}
                    >
                        <Button
                            onClick={() => setDeleteNotificationId(null)}
                            disabled={deleteLoading}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleNotificationDelete}
                            disabled={deleteLoading}
                        >
                            {deleteLoading ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Card>
        </ContentWrapper>
    )
}

export default NotificationsPage